import { getCartQuantityForVariant } from '@lib/data/cart';
import { getVariantAvailability } from '@lib/data/variants';

/**
 * Guards against stale, globally-cached `inventory_quantity` (see
 * `getVariantAvailability`) letting a customer add past what's truly left.
 * Returns `undefined` when there's no stock-based ceiling to apply (not
 * inventory-managed, or backorder allowed).
 */
export async function fetchRemainingStock(
  variantId: string,
): Promise<number | undefined> {
  const [availability, alreadyInCart] = await Promise.all([
    getVariantAvailability(variantId),
    getCartQuantityForVariant(variantId),
  ]);

  if (!availability?.manageInventory || availability.allowBackorder) {
    return undefined;
  }

  return Math.max(0, (availability.inventoryQuantity ?? 0) - alreadyInCart);
}

/**
 * Batched, server-side version for the PDP: computed once at render time for
 * every variant of the product, so the CTA is disabled and the "already in
 * your basket" message shows on first paint — no client round trip, no lag
 * while picking a variant.
 */
export async function fetchRemainingStockByVariantId(
  variantIds: string[],
): Promise<Record<string, number | undefined>> {
  const entries = await Promise.all(
    variantIds.map(async (id) => [id, await fetchRemainingStock(id)] as const),
  );

  return Object.fromEntries(entries);
}

/** `remaining === undefined` means "no stock-based ceiling applies". */
export function isStockBlocked(
  remaining: number | undefined,
  requestedQuantity: number,
): boolean {
  return remaining !== undefined && requestedQuantity > remaining;
}
